import React, {
	useState,
	useEffect,
	useLayoutEffect,
	useCallback,
	useRef,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Loader } from "@mantine/core";
import {
	IconChevronLeft,
	IconChevronRight,
	IconPlus,
	IconCursorText,
	IconTrash,
} from "@tabler/icons-react";
import { ButtonWithIcon } from "../ButtonWithIcon";
import MaterialThreeDotMenu from "../MaterialThreeDotMenu";
import MatterAiChat from "./MatterAiChat";
import {
	getAiSessions,
	updateAiSessionTitle,
	deleteAiSession,
} from "../../actions/aiChatActions";
import { openGlobalDialog } from "../../actions/globalEditDialogActions";
import {
	setFooterLeftOverride,
	clearFooterLeftOverride,
} from "../../actions/footerActions";
import { shortDateFormatOffset } from "../../utilities/utilities";
import "./MatterAiPane.css";

// must match the conversation column's own left padding in MatterAiChat.js,
// so the footer disclaimer lines up with the text field below it
const CONVERSATION_COLUMN_LEFT_PADDING = 40;
const AI_MISTAKES_DISCLAIMER =
	"LexWorkplace AI can make mistakes. Consider checking important information.";

const MatterAiPane = ({ matterID, sessionId, basePath, fullScope }) => {
	const chatBasePath = basePath ?? `/matters/${matterID}/ai`;
	const [sessions, setSessions] = useState([]);
	const [loadingSessions, setLoadingSessions] = useState(true);
	const [collapsed, setCollapsed] = useState(false);
	const [newChatKey, setNewChatKey] = useState(0);
	const [hoveredSessionId, setHoveredSessionId] = useState(null);
	const [renamingSessionId, setRenamingSessionId] = useState(null);
	const [renameValue, setRenameValue] = useState("");
	const renameInputRef = useRef(null);
	const sidebarRef = useRef(null);
	const [sidebarWidth, setSidebarWidth] = useState(0);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// measure the actual rendered sidebar width (it's a %-of-remaining-space
	// value with a min-width clamp, not a fixed number) so the footer
	// disclaimer can line up exactly with the text field to its right
	useLayoutEffect(() => {
		const el = sidebarRef.current;
		if (!el) return undefined;
		const measure = () => setSidebarWidth(el.getBoundingClientRect().width);
		measure();
		if (typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [collapsed]);

	// put the disclaimer in the app's shared Footer (in GlobalPagination's
	// slot) instead of duplicating a second footer here; clear it on unmount
	// so leaving the AI screen restores normal pagination there
	useEffect(() => {
		dispatch(
			setFooterLeftOverride({
				text: AI_MISTAKES_DISCLAIMER,
				// Footer's own left slot already applies marginLeft: 8, so
				// back that out to land exactly under the text field
				marginLeft: sidebarWidth + CONVERSATION_COLUMN_LEFT_PADDING - 8,
			})
		);
		return () => dispatch(clearFooterLeftOverride());
	}, [dispatch, sidebarWidth]);

	const loadSessions = useCallback(() => {
		getAiSessions(fullScope ? [] : [matterID])
			.then((s) => {
				// ids are DB ints; normalize to strings to match the URL param
				setSessions(
					s.map((session) => ({ ...session, id: String(session.id) }))
				);
				setLoadingSessions(false);
			})
			.catch(() => setLoadingSessions(false));
	}, [matterID, fullScope]);

	useEffect(() => {
		loadSessions();
	}, [loadSessions]);

	useEffect(() => {
		if (renamingSessionId != null) renameInputRef.current?.focus();
	}, [renamingSessionId]);

	const goToSession = (id) => navigate(`${chatBasePath}/${id}`);
	const startNewChat = () => {
		setNewChatKey((k) => k + 1);
		navigate(`${chatBasePath}/new`);
	};

	const startRename = (session) => {
		setRenamingSessionId(session.id);
		setRenameValue(session.title || "");
	};

	const saveRename = () => {
		const targetId = renamingSessionId;
		if (targetId == null) return;
		setRenamingSessionId(null);
		const newTitle = renameValue.trim();
		setSessions((prev) =>
			prev.map((s) => (s.id === targetId ? { ...s, title: newTitle } : s))
		);
		updateAiSessionTitle({ sessionId: targetId, title: newTitle }).catch(
			() => loadSessions()
		);
	};

	const handleChatSessionTitleChange = (id, newTitle) => {
		// caller may pass a DB int or a URL string id
		const targetId = String(id);
		setSessions((prev) =>
			prev.map((s) => (s.id === targetId ? { ...s, title: newTitle } : s))
		);
	};

	const confirmDeleteSession = (session) => {
		dispatch(
			openGlobalDialog(
				{
					label: "Delete Chat Conversation?",
					mainText:
						"Are you sure you want to delete this chat conversation? This action cannot be undone.",
					canSubmit: true,
					hideTextfield: true,
					submitButtonLabel: "Delete",
					redSubmitButton: true,
				},
				() => {
					deleteAiSession(session.id).then(() => {
						setSessions((prev) =>
							prev.filter((s) => s.id !== session.id)
						);
						if (session.id === sessionId) {
							navigate(`${chatBasePath}/new`);
						}
					});
				}
			)
		);
	};

	return (
		<div
			style={{
				display: "flex",
				flex: 1,
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			{/* Sidebar */}
			<div
				ref={sidebarRef}
				style={{
					width: collapsed ? 40 : "18%",
					minWidth: collapsed ? 40 : 180,
					borderRight: "1px solid #e0e0e0",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					flexShrink: 0,
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: collapsed ? "center" : "space-between",
						alignItems: "center",
						padding: 8,
						flexShrink: 0,
						flexDirection: collapsed ? "column" : "row",
					}}
				>
					{!collapsed && (
						<ButtonWithIcon
							handler={startNewChat}
							buttonText="New Chat"
							iconComponent={
								<IconPlus
									size={16}
									style={{ verticalAlign: "sub" }}
								/>
							}
						/>
					)}
					<ActionIcon
						variant="subtle"
						onClick={() => setCollapsed((c) => !c)}
						aria-label={
							collapsed ? "Expand sidebar" : "Collapse sidebar"
						}
					>
						{collapsed ? (
							<IconChevronRight size={16} />
						) : (
							<IconChevronLeft size={16} />
						)}
					</ActionIcon>
					{collapsed && (
						<span
							style={{
								fontSize: 11,
								color: "#555",
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								writingMode: "vertical-rl",
								marginTop: 8,
							}}
						>
							Chat History
						</span>
					)}
				</div>

				{!collapsed && (
					<div
						style={{
							padding: "0 8px 8px",
							display: "flex",
							flexDirection: "column",
							gap: 8,
							overflow: "auto",
							flexGrow: 1,
						}}
					>
						{loadingSessions ? (
							<div
								style={{
									textAlign: "center",
									paddingTop: 16,
								}}
							>
								<Loader size={20} />
							</div>
						) : sessions.length === 0 ? (
							<div
								style={{
									textAlign: "center",
									color: "#888",
									paddingTop: 16,
									fontSize: 13,
								}}
							>
								No chats yet
							</div>
						) : (
							sessions.map((session) => {
								const isRenaming =
									renamingSessionId === session.id;
								return (
									<div
										key={session.id}
										onClick={() =>
											!isRenaming &&
											goToSession(session.id)
										}
										onMouseEnter={() =>
											setHoveredSessionId(session.id)
										}
										onMouseLeave={() =>
											setHoveredSessionId(null)
										}
										style={{
											padding: "8px 10px",
											borderRadius: 4,
											cursor: "pointer",
											background:
												session.id === sessionId
													? "#e8e8e8"
													: "transparent",
											display: "flex",
											flexDirection: "column",
											gap: 2,
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 4,
											}}
										>
											<div
												style={{
													flexGrow: 1,
													minWidth: 0,
												}}
											>
												{isRenaming ? (
													<input
														ref={renameInputRef}
														value={renameValue}
														onChange={(e) =>
															setRenameValue(
																e.target.value
															)
														}
														onBlur={saveRename}
														onKeyDown={(e) => {
															if (
																e.key ===
																"Enter"
															)
																saveRename();
															if (
																e.key ===
																"Escape"
															)
																setRenamingSessionId(
																	null
																);
														}}
														style={{
															fontWeight: "bold",
															fontSize: 13,
															width: "100%",
															border: "none",
															borderBottom:
																"1px solid #ccc",
															outline: "none",
															background:
																"transparent",
															fontFamily:
																"inherit",
														}}
													/>
												) : (
													<div
														style={{
															fontWeight: "bold",
															fontSize: 13,
															whiteSpace:
																"nowrap",
															overflow: "hidden",
															textOverflow:
																"ellipsis",
														}}
													>
														{session.title ||
															"Untitled Chat"}
													</div>
												)}
											</div>
											<div
												onClick={(e) =>
													e.stopPropagation()
												}
												style={{
													flexShrink: 0,
													visibility:
														!isRenaming &&
														hoveredSessionId ===
															session.id
															? "visible"
															: "hidden",
												}}
											>
												<MaterialThreeDotMenu
													className="MatterAiThreeDotTrigger"
													position="relative"
													menuItems={[
														{
															text: "Rename",
															onSelect: () =>
																startRename(
																	session
																),
															iconComponent: (
																<IconCursorText
																	size={16}
																	style={{
																		verticalAlign:
																			"sub",
																	}}
																/>
															),
														},
														{
															text: "Delete",
															color: "red",
															onSelect: () =>
																confirmDeleteSession(
																	session
																),
															iconComponent: (
																<IconTrash
																	size={16}
																	color="red"
																	style={{
																		verticalAlign:
																			"sub",
																	}}
																/>
															),
														},
													]}
												/>
											</div>
										</div>
										<div
											style={{
												fontSize: 11,
												color: "#666",
											}}
										>
											{shortDateFormatOffset(
												session.lastUpdated
											)}
										</div>
									</div>
								);
							})
						)}
					</div>
				)}
			</div>

			{/* Main area */}
			<div
				style={{
					flexGrow: 1,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<MatterAiChat
					key={
						sessionId === "new"
							? `new-${newChatKey}`
							: sessionId || "new"
					}
					matterID={matterID}
					sessionId={sessionId || "new"}
					basePath={chatBasePath}
					fullScope={fullScope}
					onNewSession={loadSessions}
					onSessionTitleChange={handleChatSessionTitleChange}
				/>
			</div>
		</div>
	);
};

export default MatterAiPane;
